import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchSimCalls, fetchSimCallAudio, fetchUsersList, bulkConvertSimCallsToLeads } from '../services/api';
import { useSuperTenant } from '../context/SuperAdminTenantContext';
import { useAuth } from '../context/AuthContext';
import type { SimCall, User } from '../types';
import { encodeId } from '../utils/hashids';
import Pager from '../components/Pager';

// Roles that see the agent filter dropdown. Agent-role users can only see
// their own calls anyway (backend-enforced) so the dropdown is useless
// for them; hiding it also prevents visual clutter.
const AGENT_FILTER_ROLES = ['tenant_admin', 'manager', 'marketing', 'super_admin'];

// Lazy-loads audio blob on first play (auth-gated, not a public URL).
// Renders as a circular button until the user clicks; once loaded, expands
// into a compact audio control below the row on click.
const AudioPlayer: React.FC<{ simCallId: number; recordingUrl: string | null }> = ({ simCallId, recordingUrl }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(recordingUrl);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(false);

  const toggle = () => {
    if (blobUrl) { setExpanded(v => !v); return; }
    setLoading(true);
    fetchSimCallAudio(simCallId)
      .then(url => { setBlobUrl(url); setExpanded(true); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  if (error) return <span style={{ fontSize: 12, color: '#dc2626' }}>Failed to load</span>;

  return (
    <div>
      <button
        onClick={toggle}
        disabled={loading}
        title={expanded ? 'Close' : 'Play recording'}
        style={{
          background: expanded ? '#7c3aed' : '#ede9fe',
          color:      expanded ? '#fff'    : '#7c3aed',
          border: 'none', borderRadius: '50%',
          width: 30, height: 30, cursor: loading ? 'default' : 'pointer',
          fontSize: 14, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {loading ? '…' : (expanded ? '⏹' : '▶')}
      </button>
      {expanded && blobUrl && (
        <audio controls autoPlay src={blobUrl} style={{ height: 32, marginLeft: 8, verticalAlign: 'middle', width: 200 }} />
      )}
    </div>
  );
};

function fmtDuration(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

const ACTION_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  matched:       { label: 'Matched',        color: '#059669', bg: '#d1fae5' },
  created:       { label: 'New Lead',       color: '#0369a1', bg: '#e0f2fe' },
  not_converted: { label: 'Not Converted',  color: '#6b7280', bg: '#f3f4f6' },
};

const CALL_STATUS_STYLE: Record<string, { color: string; bg: string }> = {
  connected: { color: '#166534', bg: '#dcfce7' },
  missed:    { color: '#b91c1c', bg: '#fee2e2' },
  no_answer: { color: '#b91c1c', bg: '#fee2e2' },
  busy:      { color: '#9a3412', bg: '#ffedd5' },
  rejected:  { color: '#9a3412', bg: '#ffedd5' },
  failed:    { color: '#9a3412', bg: '#ffedd5' },
};

// Palette copied from CallReportsPage so avatars pick the same colours per idx.
const COLORS = ['#7c3aed', '#0891b2', '#059669', '#d97706', '#dc2626', '#4f46e5', '#db2777', '#0d9488'];

const initialsOf = (name: string) =>
  name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const Avatar: React.FC<{ name: string; size?: number; idx?: number }> = ({ name, size = 32, idx = 0 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', background: COLORS[idx % COLORS.length],
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.35, fontWeight: 700, flexShrink: 0,
  }}>{initialsOf(name)}</div>
);

interface SimCallRow extends SimCall {
  has_audio?: boolean;
  device?: { id: number; device_name: string | null; user?: { id: number; name: string } };
  lead?: { id: number; name: string; company: string | null; mobile: string | null } | null;
  direction?: 'incoming' | 'outgoing' | null;
  call_status?: string | null;
}

const SimCallsPage: React.FC = () => {
  const history = useHistory();
  const { tenantId } = useSuperTenant();
  const { user } = useAuth();
  const canFilterByAgent = !!user && AGENT_FILTER_ROLES.includes(user.role);

  const [calls, setCalls] = useState<SimCallRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');

  // ── Filters ────────────────────────────────────────────────────────────
  const [q, setQ]                 = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [direction, setDirection] = useState<'' | 'incoming' | 'outgoing'>('');
  const [status, setStatus]       = useState<string>('');
  const [dateFrom, setDateFrom]   = useState<string>('');
  const [dateTo, setDateTo]       = useState<string>('');
  const [agentId, setAgentId]     = useState<string>('');
  const [agents, setAgents]       = useState<User[]>([]);
  // ── Bulk convert ─────────────────────────────────────────────────────
  // Set of sim_call_ids selected via checkbox. Only rows with no lead
  // linked yet (i.e. action null / not_converted) show a checkbox.
  const [selected, setSelected]   = useState<Set<number>>(new Set());
  const [converting, setConverting] = useState(false);
  // Same role gate as the agent filter — bulk conversion is an admin action.
  const canBulkConvert = canFilterByAgent;

  // Debounce free-text search — avoid a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q.trim()), 400);
    return () => clearTimeout(id);
  }, [q]);

  // Load agent list once for the dropdown — only for privileged roles.
  // fetchUsersList is tenant-scoped by the auth token, so tenant admins/
  // managers only ever see their own tenant's agents.
  useEffect(() => {
    if (!canFilterByAgent) return;
    fetchUsersList()
      .then((all: User[]) => setAgents(
        all.filter(u => u.role === 'agent' || u.role === 'manager')
           .sort((a, b) => a.name.localeCompare(b.name))
      ))
      .catch(() => {});
  }, [canFilterByAgent, tenantId]);

  const filtersEmpty = !qDebounced && !direction && !status && !dateFrom && !dateTo && !agentId;
  const resetFilters = () => {
    setQ(''); setQDebounced(''); setDirection(''); setStatus(''); setDateFrom(''); setDateTo(''); setAgentId('');
  };

  const load = (p: number) => {
    setLoading(true);
    fetchSimCalls({
      page: p,
      tenant_id:   tenantId || undefined,
      q:           qDebounced || undefined,
      direction:   direction || undefined,
      call_status: status || undefined,
      date_from:   dateFrom || undefined,
      date_to:     dateTo || undefined,
      agent_id:    agentId ? parseInt(agentId, 10) : undefined,
    })
      .then(res => {
        setCalls(res.data);
        setLastPage(res.last_page);
        setTotal(res.total);
        setPage(res.current_page);
      })
      .catch(() => setError('Failed to load call records'))
      .finally(() => setLoading(false));
  };

  // Reload whenever the tenant or any filter changes — always jump to page 1
  // when a filter changes so we don't sit on an out-of-range page number.
  useEffect(() => { load(1); }, [tenantId, qDebounced, direction, status, dateFrom, dateTo, agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear selection whenever the visible page changes (page nav or filter
  // reload) — stale IDs from a previous page shouldn't linger.
  useEffect(() => { setSelected(new Set()); }, [page, calls]);

  // IDs on the current page that CAN be converted (no lead linked yet).
  const convertibleIds = calls
    .filter(c => !c.lead_id)
    .map(c => c.id);
  const allConvertibleSelected = convertibleIds.length > 0
    && convertibleIds.every(id => selected.has(id));

  const toggleOne = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllOnPage = () => {
    setSelected(prev => {
      if (allConvertibleSelected) {
        // Un-select the ones from this page; leave any others alone.
        const next = new Set(prev);
        convertibleIds.forEach(id => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      convertibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleBulkConvert = async () => {
    if (selected.size === 0 || converting) return;
    const ids = Array.from(selected);
    setConverting(true);
    try {
      const res = await bulkConvertSimCallsToLeads(ids);
      const msg = `Processed ${res.processed} · created ${res.created} · matched ${res.matched}`
        + (res.skipped?.length ? ` · skipped ${res.skipped.length}` : '')
        + (res.errors?.length ? ` · errors ${res.errors.length}` : '');
      // eslint-disable-next-line no-alert
      alert(msg);
      setSelected(new Set());
      load(page);
    } catch (e: any) {
      // eslint-disable-next-line no-alert
      alert(`Bulk convert failed: ${e?.response?.data?.message ?? e?.message ?? 'unknown error'}`);
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="list-page">
      {/* Header — same shape as CallReportsPage */}
      <div className="page-hd">
        <div className="page-hd-left">
          <h1 className="page-title">SIM Call Records</h1>
          <p className="page-meta">
            {total} call{total !== 1 ? 's' : ''} uploaded from Android devices
          </p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* ── Search + filter bar ─────────────────────────────────────── */}
      <div className="data-card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search phone, lead name, agent…"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="form-input"
            style={{ flex: '1 1 240px', minWidth: 200, fontSize: 13 }}
          />

          <select
            value={direction}
            onChange={e => setDirection(e.target.value as '' | 'incoming' | 'outgoing')}
            className="filter-select"
            style={{ fontSize: 13 }}
            title="Direction"
          >
            <option value="">All directions</option>
            <option value="incoming">Incoming</option>
            <option value="outgoing">Outgoing</option>
          </select>

          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="filter-select"
            style={{ fontSize: 13 }}
            title="Call status"
          >
            <option value="">All statuses</option>
            <option value="connected">Connected</option>
            <option value="missed">Missed</option>
            <option value="no_answer">No answer</option>
            <option value="busy">Busy</option>
            <option value="rejected">Rejected</option>
            <option value="failed">Failed</option>
          </select>

          {canFilterByAgent && (
            <select
              value={agentId}
              onChange={e => setAgentId(e.target.value)}
              className="filter-select"
              style={{ fontSize: 13, minWidth: 160 }}
              title="Agent"
            >
              <option value="">All agents</option>
              {agents.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.role === 'manager' ? ' (manager)' : ''}
                </option>
              ))}
            </select>
          )}

          <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            From
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="form-input"
              style={{ fontSize: 13, padding: '4px 8px' }}
            />
          </label>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            To
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="form-input"
              style={{ fontSize: 13, padding: '4px 8px' }}
            />
          </label>

          {!filtersEmpty && (
            <button
              type="button"
              onClick={resetFilters}
              className="btn btn-ghost"
              style={{ fontSize: 12, padding: '5px 10px' }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Bulk convert bar — only for privileged roles, only when 1+ selected */}
      {canBulkConvert && selected.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 14px', marginBottom: 10,
          background: 'var(--primary-light)', border: '1px solid var(--primary)',
          borderRadius: 8,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>
            {selected.size} call{selected.size === 1 ? '' : 's'} selected
          </span>
          <button
            type="button"
            onClick={handleBulkConvert}
            disabled={converting}
            className="btn btn-primary"
            style={{ fontSize: 13, padding: '6px 14px' }}
          >
            {converting ? 'Converting…' : `Convert ${selected.size} to Lead${selected.size === 1 ? '' : 's'}`}
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            disabled={converting}
            className="btn btn-ghost"
            style={{ fontSize: 12, padding: '5px 10px' }}
          >
            Clear selection
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
            Each new lead is assigned to the calling agent
          </span>
        </div>
      )}

      <div className="data-card">
        <div className="data-card-wrap">
          <table>
            <thead>
              <tr>
                {canBulkConvert && (
                  <th style={{ width: 28 }}>
                    <input
                      type="checkbox"
                      title={convertibleIds.length > 0
                        ? `Select ${convertibleIds.length} convertible call${convertibleIds.length === 1 ? '' : 's'} on this page`
                        : 'No convertible calls on this page'}
                      checked={allConvertibleSelected}
                      disabled={convertibleIds.length === 0}
                      onChange={toggleAllOnPage}
                    />
                  </th>
                )}
                <th>#</th>
                <th>Agent</th>
                <th>Called Number</th>
                <th>Lead</th>
                <th>When</th>
                <th>Dir</th>
                <th>Duration</th>
                <th>Action</th>
                <th>Status</th>
                <th>Rec</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={canBulkConvert ? 11 : 10} style={{ textAlign: 'center', padding: 24 }}>Loading…</td></tr>
              )}

              {!loading && calls.length === 0 && (
                <tr className="table-empty-row">
                  <td colSpan={canBulkConvert ? 11 : 10}>No SIM calls uploaded yet.</td>
                </tr>
              )}

              {!loading && calls.map((call, i) => {
                const actionStyle = call.action ? ACTION_STYLE[call.action] : ACTION_STYLE.not_converted;
                const callStatus  = call.call_status ?? null;
                const callSty     = callStatus ? CALL_STATUS_STYLE[callStatus] : null;
                const dir         = call.direction ?? null;
                const agentName   = call.device?.user?.name ?? 'Unknown';
                const rowNum      = ((page - 1) * 20) + i + 1;

                const isConvertible = !call.lead_id;
                return (
                  <tr key={call.id}>
                    {canBulkConvert && (
                      <td style={{ width: 28 }}>
                        {isConvertible ? (
                          <input
                            type="checkbox"
                            checked={selected.has(call.id)}
                            onChange={() => toggleOne(call.id)}
                            title="Select for bulk convert to lead"
                          />
                        ) : (
                          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>
                    )}
                    {/* # */}
                    <td className="td-muted">{rowNum}</td>

                    {/* Agent (avatar + name + device) */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar name={agentName} idx={i} size={28} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{agentName}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            {call.device?.device_name ?? `Device #${call.device_id}`}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Called Number */}
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13, fontFamily: 'monospace' }}>
                        {call.called_phone}
                      </div>
                      {call.caller_phone && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          from {call.caller_phone}
                        </div>
                      )}
                    </td>

                    {/* Lead */}
                    <td>
                      {call.lead ? (
                        <button
                          onClick={() => history.push(`/leads/${encodeId(call.lead!.id)}`)}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)' }}>
                            {call.lead.name}
                          </div>
                          {call.lead.company && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              {call.lead.company}
                            </div>
                          )}
                        </button>
                      ) : <span className="td-muted">—</span>}
                    </td>

                    {/* When */}
                    <td>
                      <div style={{ fontSize: 13 }}>
                        {new Date(call.call_time?.call_started_at ?? call.started_at).toLocaleTimeString('en-IN', {
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {new Date(call.call_time?.call_started_at ?? call.started_at).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short',
                        })}
                      </div>
                    </td>

                    {/* Dir */}
                    <td>
                      {dir
                        ? <span style={{
                            background: dir === 'incoming' ? '#dbeafe' : '#dcfce7',
                            color:      dir === 'incoming' ? '#1d4ed8' : '#166534',
                            borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                          }}>
                            {dir === 'incoming' ? '↙ IN' : '↗ OUT'}
                          </span>
                        : <span className="td-muted">—</span>}
                    </td>

                    {/* Duration */}
                    <td className="td-muted" style={{ whiteSpace: 'nowrap' }}>
                      {call.duration_seconds > 0 ? fmtDuration(call.duration_seconds) : '—'}
                    </td>

                    {/* Action pill (matched/created/not_converted) */}
                    <td>
                      <span style={{
                        padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                        color: actionStyle.color, background: actionStyle.bg,
                      }}>
                        {actionStyle.label}
                      </span>
                    </td>

                    {/* Call status pill */}
                    <td>
                      {callStatus
                        ? <span style={{
                            borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                            ...(callSty ?? { bg: '#f1f5f9', color: '#475569' }),
                          }}>
                            {callStatus.toUpperCase()}
                          </span>
                        : <span className="td-muted">—</span>}
                    </td>

                    {/* Recording */}
                    <td>
                      {call.recording_url || call.has_audio
                        ? <AudioPlayer simCallId={call.id} recordingUrl={call.recording_url} />
                        : <span className="td-muted">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pager currentPage={page} lastPage={lastPage} total={total} label="SIM calls" onChange={load} />
      </div>
    </div>
  );
};

export default SimCallsPage;
